/**
 * Engine Diagnostics model — turns flat SignalK propulsion paths into per-engine tiles.
 *
 * Sources per engine id (port, starboard, 0, 1 ...):
 *   - NMEA 2000 paths         propulsion.<id>.revolutions / temperature / alternatorVoltage / ...
 *   - Fox Marine MEFI gateway propulsion.<id>.foxMarine.*   (Bluetooth, published by the Cerbo plugin)
 *
 * Pure functions only (no imports) so they can be unit tested from backend/scripts/qa/run-unit.js.
 * `read(path)` is the live → database reader from powerFlow.js (createPowerReader) and returns
 * { value, units, timestamp, source } or null. Priority is never changed here: live → database → demo.
 *
 * SignalK SI units on the wire: Hz, ratio 0..1, Kelvin, Pascal, m³/s, seconds, radians, volts.
 * `planned: true` fields are published by the plugin only once decoded; until then they show "not mapped yet".
 */

export const LIVE_MAX_AGE_MS = 15 * 60 * 1000;

const M3S_TO_GPH = 15850.3231 * 3600;

const CONVERT = {
  rpm: (v) => v * 60,
  percent: (v) => v * 100,
  tempF: (v) => (v - 273.15) * 1.8 + 32,
  volts: (v) => v,
  gph: (v) => v * M3S_TO_GPH,
  hours: (v) => v / 3600,
  psi: (v) => v * 0.000145038,
  inHg: (v) => v * 0.0002953,
  deg: (v) => (v * 180) / Math.PI,
  ms: (v) => v * 1000,
  raw: (v) => v,
};

const UNIT_LABEL = {
  rpm: 'rpm', percent: '%', tempF: '°F', volts: 'V', gph: 'gph', hours: 'h', psi: 'psi', inHg: 'inHg', deg: '°', ms: 'ms', raw: '',
};

/**
 * Field registry. `paths` are suffixes after `propulsion.<id>.`, in priority order.
 * N2K paths come first where both exist; the BLE value is used when N2K has nothing for that engine.
 */
export const ENGINE_FIELDS = [
  // Engine
  { key: 'rpm', group: 'engine', label: 'RPM', paths: ['revolutions', 'foxMarine.revolutions'], kind: 'rpm', decimals: 0, big: true },
  { key: 'load', group: 'engine', label: 'Engine load', paths: ['engineLoad'], kind: 'percent', decimals: 0 },
  { key: 'throttle', group: 'engine', label: 'Throttle', paths: ['foxMarine.throttle'], kind: 'percent', decimals: 1 },
  { key: 'coolant', group: 'engine', label: 'Coolant temp', paths: ['temperature'], kind: 'tempF', decimals: 0 },
  { key: 'fuelRate', group: 'engine', label: 'Fuel rate', paths: ['fuel.rate'], kind: 'gph', decimals: 1 },
  { key: 'battery', group: 'engine', label: 'Battery / alternator', paths: ['alternatorVoltage'], kind: 'volts', decimals: 2 },
  { key: 'runTime', group: 'engine', label: 'Engine hours', paths: ['runTime'], kind: 'hours', decimals: 1 },
  { key: 'oilPressure', group: 'engine', label: 'Oil pressure', paths: ['oilPressure'], kind: 'psi', decimals: 0 },
  // Air, fuel and ignition (Fox only)
  { key: 'tps', group: 'sensors', label: 'TPS voltage', paths: ['foxMarine.tpsVoltage'], kind: 'volts', decimals: 2 },
  { key: 'mapVolts', group: 'sensors', label: 'MAP voltage', paths: ['foxMarine.mapVoltage'], kind: 'volts', decimals: 2 },
  { key: 'iac', group: 'sensors', label: 'IAC position', paths: ['foxMarine.iacPosition'], kind: 'raw', decimals: 0 },
  { key: 'mat', group: 'sensors', label: 'Manifold air temp', paths: ['foxMarine.manifoldAirTemp'], kind: 'tempF', decimals: 0, planned: true },
  { key: 'map', group: 'sensors', label: 'Manifold pressure', paths: ['foxMarine.manifoldPressure'], kind: 'inHg', decimals: 2, planned: true },
  { key: 'baro', group: 'sensors', label: 'Barometric pressure', paths: ['foxMarine.baroPressure'], kind: 'inHg', decimals: 2, planned: true },
  { key: 'spark', group: 'sensors', label: 'Spark advance', paths: ['foxMarine.sparkAdvance'], kind: 'deg', decimals: 1, planned: true },
  { key: 'knock', group: 'sensors', label: 'Knock retard', paths: ['foxMarine.knockRetard'], kind: 'deg', decimals: 1, planned: true },
  { key: 'cam', group: 'sensors', label: 'Cam retard', paths: ['foxMarine.camRetard'], kind: 'deg', decimals: 1, planned: true },
  { key: 'octane', group: 'sensors', label: 'Octane rating', paths: ['foxMarine.octaneRating'], kind: 'percent', decimals: 1, planned: true },
  { key: 'injA', group: 'sensors', label: 'Injector A on time', paths: ['foxMarine.injectorATime'], kind: 'ms', decimals: 2, planned: true },
  { key: 'injB', group: 'sensors', label: 'Injector B on time', paths: ['foxMarine.injectorBTime'], kind: 'ms', decimals: 2, planned: true },
  { key: 'idleTarget', group: 'sensors', label: 'Desired idle RPM', paths: ['foxMarine.desiredIdleRpm'], kind: 'raw', decimals: 0, planned: true },
];

/** On/off flags the gateway reports (0 = normal, 1 = active). Published once decoded. */
export const ENGINE_FLAGS = [
  { key: 'checkEngine', label: 'Check engine lamp', path: 'foxMarine.checkEngineLamp' },
  { key: 'checkGauges', label: 'Check gauges lamp', path: 'foxMarine.checkGaugesLamp' },
  { key: 'overTemp', label: 'Over temperature', path: 'foxMarine.overTemperature' },
  { key: 'lowOilPressure', label: 'Low oil pressure', path: 'foxMarine.lowOilPressure' },
  { key: 'lowOilLevel', label: 'Low oil level', path: 'foxMarine.lowOilLevel' },
  { key: 'lowVoltage', label: 'Low system voltage', path: 'foxMarine.lowSystemVoltage' },
  { key: 'lowFuelPressure', label: 'Low fuel pressure', path: 'foxMarine.lowFuelPressure' },
  { key: 'warning1', label: 'General warning 1', path: 'foxMarine.generalWarning1' },
  { key: 'warning2', label: 'General warning 2', path: 'foxMarine.generalWarning2' },
  { key: 'knocking', label: 'Engine knocking', path: 'foxMarine.knocking' },
  { key: 'estop', label: 'E-stop input', path: 'foxMarine.eStopInput' },
];

function parseNum(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function ageMs(ts, now) {
  if (!ts) return Infinity;
  const t = new Date(ts).getTime();
  return Number.isNaN(t) ? Infinity : Math.max(0, now - t);
}

const ENGINE_PATH_RE = /^propulsion\.([^.]+)\.(revolutions|temperature|alternatorVoltage|engineLoad|runTime|foxMarine\..+)$/;

/** Engine ids that have data, ordered port, starboard, then the rest alphabetically. */
export function listEngineIds(paths) {
  const ids = new Set();
  (paths || []).forEach((p) => {
    const m = ENGINE_PATH_RE.exec(p);
    if (m) ids.add(m[1]);
  });
  const rank = (id) => (id === 'port' ? 0 : id === 'starboard' ? 1 : 2);
  return Array.from(ids).sort((a, b) => rank(a) - rank(b) || String(a).localeCompare(String(b)));
}

export function hasEngineData(paths) {
  return listEngineIds(paths).length > 0;
}

export function engineLabel(id) {
  if (id === 'port') return 'Port engine';
  if (id === 'starboard') return 'Starboard engine';
  return `Engine ${id}`;
}

export function formatEngineValue(raw, kind, decimals) {
  const n = parseNum(raw);
  if (n === null) return null;
  const conv = CONVERT[kind] || CONVERT.raw;
  const v = conv(n);
  return Number.isFinite(v) ? v.toFixed(decimals) : null;
}

function readField(read, id, field) {
  for (const suffix of field.paths) {
    const r = read(`propulsion.${id}.${suffix}`);
    const n = r ? parseNum(r.value) : null;
    if (r && n !== null) return { r, n, via: suffix.startsWith('foxMarine.') ? 'ble' : 'n2k' };
  }
  return null;
}

/** State of the Bluetooth link for one engine. */
export function bluetoothLink(read, id) {
  const get = (k) => {
    const r = read(`propulsion.${id}.foxMarine.${k}`);
    return r ? parseNum(r.value) : null;
  };
  const connected = get('bleConnected');
  const ecu = get('ecuOnline');
  const verified = get('verified');
  const hasAny = [connected, ecu, verified, get('revolutions')].some((x) => x !== null);
  if (!hasAny) return { state: 'none', text: 'No Bluetooth gateway data (showing NMEA 2000 values only)' };
  if (connected === 0) return { state: 'offline', text: 'Gateway not connected' };
  if (ecu === 0) return { state: 'ecu-off', text: 'Gateway connected, engine ECU is off' };
  if (verified === 1) return { state: 'verified', text: 'Bluetooth data verified against NMEA 2000' };
  return { state: 'live', text: 'Bluetooth data flowing' };
}

export function buildEngineModel(paths, read, { now = Date.now() } = {}) {
  const ids = listEngineIds(paths);
  let newest = 0;
  const sources = new Set();
  const engines = ids.map((id) => {
    const fields = ENGINE_FIELDS.map((f) => {
      const hit = readField(read, id, f);
      if (!hit) {
        return {
          key: f.key, group: f.group, label: f.label, big: Boolean(f.big),
          available: false, display: null, unit: UNIT_LABEL[f.kind], planned: Boolean(f.planned),
          bleOnly: f.paths.every((p) => p.startsWith('foxMarine.')),
        };
      }
      const t = hit.r.timestamp ? new Date(hit.r.timestamp).getTime() : 0;
      if (t > newest) newest = t;
      sources.add(hit.r.source);
      // Both sources: show the BLE reading as a cross-check under the N2K one.
      let sub = null;
      if (hit.via === 'n2k' && f.paths.length > 1) {
        const ble = read(`propulsion.${id}.${f.paths[f.paths.length - 1]}`);
        const bn = ble ? parseNum(ble.value) : null;
        if (bn !== null) sub = `Bluetooth: ${formatEngineValue(bn, f.kind, f.decimals)} ${UNIT_LABEL[f.kind]}`;
      }
      return {
        key: f.key, group: f.group, label: f.label, big: Boolean(f.big),
        available: true, display: formatEngineValue(hit.n, f.kind, f.decimals), unit: UNIT_LABEL[f.kind],
        via: hit.via, source: hit.r.source, sub, planned: Boolean(f.planned),
        bleOnly: f.paths.every((p) => p.startsWith('foxMarine.')),
        stale: ageMs(hit.r.timestamp, now) > LIVE_MAX_AGE_MS,
      };
    });

    const flags = ENGINE_FLAGS.map((fl) => {
      const r = read(`propulsion.${id}.${fl.path}`);
      const n = r ? parseNum(r.value) : null;
      return { key: fl.key, label: fl.label, state: n === null ? 'unknown' : n >= 1 ? 'alarm' : 'ok' };
    });

    return {
      id,
      label: engineLabel(id),
      link: bluetoothLink(read, id),
      fields,
      flags,
      flagsAvailable: flags.some((f) => f.state !== 'unknown'),
      activeAlarms: flags.filter((f) => f.state === 'alarm').map((f) => f.label),
      mappedCount: fields.filter((f) => f.available && f.bleOnly).length,
      plannedCount: fields.filter((f) => f.planned && !f.available).length,
    };
  });

  let source = 'database';
  if (sources.has('live')) source = 'live';
  else if (sources.has('stale')) source = 'stale';
  return { engines, source, lastUpdate: newest ? new Date(newest).toISOString() : null };
}

/** Demo sample (clearly labelled in the page) for vessels with no engine data at all. */
export function buildDemoEngineFlat() {
  const ts = new Date().toISOString();
  const e = (value) => ({ value, units: '', timestamp: ts });
  const eng = (id, rpm, tempK) => ({
    [`propulsion.${id}.revolutions`]: e(rpm / 60),
    [`propulsion.${id}.engineLoad`]: e(0.12),
    [`propulsion.${id}.temperature`]: e(tempK),
    [`propulsion.${id}.alternatorVoltage`]: e(13.9),
    [`propulsion.${id}.fuel.rate`]: e(0.0000000207),
    [`propulsion.${id}.runTime`]: e(3162000),
    [`propulsion.${id}.foxMarine.revolutions`]: e(rpm / 60),
    [`propulsion.${id}.foxMarine.throttle`]: e(0.035),
    [`propulsion.${id}.foxMarine.tpsVoltage`]: e(0.78),
    [`propulsion.${id}.foxMarine.mapVoltage`]: e(1.35),
    [`propulsion.${id}.foxMarine.iacPosition`]: e(0),
    [`propulsion.${id}.foxMarine.bleConnected`]: e(1),
    [`propulsion.${id}.foxMarine.ecuOnline`]: e(1),
    [`propulsion.${id}.foxMarine.verified`]: e(1),
  });
  return { ...eng('port', 1180, 337), ...eng('starboard', 1215, 336) };
}
